// default
import { Module } from 'vuex'
import config from '../../../config/index'
import { RootState } from '../../../store/state_models'

// types
import { WhatsAppTemplate, PhoneNumber, CountdownTimerApiResponse, CountdownTimerLink } from '../types/WhatsAppSettings'

// services
import { whatsAppService } from '../../../services/WhatsAppService'
import { mediaService } from '../../../services/MediaService'
import CalendarService from '../../../services/CalendarService'
import { getTagOptions } from '../../../util/merge_tags'

// utils
import { normalizeCountdownTimer, toNormalizedIdSet } from '../utils/WhatsAppSettings'

// Store state interface
export interface WhatsAppSettingsStoreModule {
  isConfigured: boolean | null
  isSubscribed: boolean | null
  isPaymentPending: boolean | null
  companyHasWallet: boolean | null
  templates: Array<WhatsAppTemplate>
  phoneNumbers: Array<PhoneNumber>
  countdownLinks: Array<CountdownTimerLink>
  onboardingChecklist: null | Record<string, string>
  wabaId: string | null
}

export const whatsAppSettings: Module<WhatsAppSettingsStoreModule, RootState> = {
  namespaced: true,
  state: {
    locationId: null,
    isConfigured: null,
    onboardingErrorMessage: null,
    isSubscribed: null,
    isPaymentPending: null,
    companyHasWallet: null,
    offeringEnabled: null,
    templates: [],
    folders: [],
    phoneNumbers: [],
    flows: [],
    countdownLinks: [] as CountdownTimerLink[],
    onboardingChecklist: null,
    wabaId: null,
  } as WhatsAppSettingsStoreModule,
  getters: {
    companyHasWallet: state => {
      return state.companyHasWallet
    },
    offeringEnabled: state => {
      return state.offeringEnabled
    },
    isLocationSubscribed: state => {
      return state.isSubscribed
    },
    isConfigured: state => {
      return state.isConfigured
    },
    onboardingErrorMessage: state => {
      return state.onboardingErrorMessage
    },
    isPaymentPending: state => {
      return state.isPaymentPending
    },
    onboardingChecklist: state => {
      return state.onboardingChecklist
    },
    wabaId: state => {
      return state.wabaId
    },
    countdownLinks: state => {
      return state.countdownLinks
    },
  },
  mutations: {
    setIsConfigured(state, value: { isConfigured: boolean; onboardingErrorMessage: string }) {
      state.isConfigured = value.isConfigured
      state.onboardingErrorMessage = value.onboardingErrorMessage
    },
    setIsSubscribed(state, value: boolean) {
      state.isSubscribed = value
    },
    setIsPaymentPending(state, value: boolean) {
      state.isPaymentPending = value
    },
    setCompanyHasWallet(state, value: boolean) {
      state.companyHasWallet = value
    },
    setOfferingEnabled(state, value: boolean) {
      state.offeringEnabled = value
    },
    setPhoneNumbers(state, value: Array<PhoneNumber>) {
      state.phoneNumbers = value
    },
    setDefaultPhoneNumber(state, value: string) {
      state.phoneNumbers.forEach(phoneNumber => {
        phoneNumber.default = false
      })
      const phoneNumber = state.phoneNumbers.find(phoneNumber => phoneNumber.id === value)
      if (phoneNumber) {
        phoneNumber.default = true
      }
    },
    setLocationId(state, value: string) {
      state.locationId = value
    },
    setTemplates(state, value: Array<WhatsAppTemplate>) {
      state.templates = value
    },
    setTemplateFolders(state, value: any) {
      state.folders = value
    },
    setFlows(state, value) {
      state.flows = value
    },
    setOnboardingChecklist(state, value: Record<string, string>) {
      state.onboardingChecklist = value
    },
    setWabaId(state, value: string) {
      state.wabaId = value
    },
    setCountdownLinks(state, value: Array<CountdownTimerLink>) {
      state.countdownLinks = value
    },
  },
  actions: {
    reset({ commit }) {
      commit('setIsConfigured', false)
      commit('setIsSubscribed', false)
      commit('setIsPaymentPending', false)
      commit('setWabaId', null)
    },
    async isConfigured({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res: {
          isConfigured: boolean
          onboardingErrorMessage: string
        } = await whatsAppService.isConfigured(locationId)
        commit('setIsConfigured', res)
        return res.isConfigured
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async companyHasWallet({ commit, state, rootState }) {
      try {
        if (state.companyHasWallet !== null) {
          return state.companyHasWallet
        }
        const companyId = rootState?.company.company.id as string
        const res = await whatsAppService.companyHasWallet(companyId)
        commit('setCompanyHasWallet', res.isExist)
        return Promise.resolve(res.isExist)
      } catch (error) {
        return false
      }
    },
    async onboard({ dispatch, rootState }, data) {
      const locationId = rootState?.locations.currentLocation.id as string
      const companyId = rootState?.company.company.id as string

      const baseOauthURL = `https://facebook.com/${config.whatsappMetaAppVersion}/dialog/oauth`
      const defaultParams = 'display=popup&response_type=code&override_default_response_type=true'
      const scopes = 'business_management,whatsapp_business_management,whatsapp_business_messaging'
      const extras: any = {
        feature: 'whatsapp_embedded_signup',
        setup: {},
        business_config: {},
      }
      extras.featureType = data.featureType
      const redirect_uri = `${config.ghlRevexBackendUrl}/whatsapp/onboard/oauth/finish`
      const state = btoa(JSON.stringify({ locationId, companyId, featureType: extras.featureType }))

      const link = `${baseOauthURL}?${defaultParams}&client_id=${
        config.whatsappMetaAppId
      }&scope=${scopes}&extras=${JSON.stringify(extras)}&redirect_uri=${redirect_uri}&state=${state}`

      const encodeUrl = encodeURI(link)
      const height = 562,
        width = 670
      const subWindow: any = window.open(encodeUrl, 'popUpWindow', `height=${height},width=${width}`)

      const isIntegrationPromise = new Promise((resolve, _reject) => {
        const retries = {
          current: 0,
          max: 3,
        }

        const interval = setInterval(async () => {
          if (subWindow?.closed) {
            retries.current++
            const isConfigured = await dispatch('isConfigured')

            const resolveFun = (status: boolean) => {
              clearInterval(interval)
              resolve(status)
            }

            if (isConfigured) {
              resolveFun(true)
            } else {
              if (retries.current >= retries.max) {
                resolveFun(false)
              }
            }
          }
        }, 3e3)
      })
      const isIntegrationDone = await isIntegrationPromise
      if (isIntegrationDone) {
        // we dont need to wait for this to complete, it will be done in the background
        whatsAppService.setupFlows(locationId)
      }
      return
    },
    async resync({ rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        await whatsAppService.resync({
          locationId,
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async delete({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        await whatsAppService.delete({
          locationId,
        })
        commit('setIsConfigured', {
          isConfigured: false,
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async disconnect({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        await whatsAppService.disconnect({
          locationId,
        })
        commit('setIsConfigured', {
          isConfigured: false,
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getAccountInfo({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const data = await whatsAppService.getAccountInfo({
          locationId,
        })

        commit('setWabaId', data.whatsAppBusinessAccountId)
        return Promise.resolve(data)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getPhoneNumbers({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getWhatsAppPhoneNumbersForLocationId(locationId)
        commit('setPhoneNumbers', res)
        return res
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async makeDefaultPhoneNumber({ commit, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.makeDefaultPhoneNumber(locationId, data.id)
        commit('setDefaultPhoneNumber', data.id)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getWhatsAppTemplates({ commit, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string

        // if fetchFromStore is true and locationId is the same as the one in the store, return the templates from the store
        if (data?.fetchFromStore === true && rootState?.whatsAppSettings.locationId === locationId) {
          const templates = rootState?.whatsAppSettings.templates
          if (templates.length > 0) {
            return Promise.resolve(templates)
          }
        }

        const templateId = (data?.templateId as string) || ''
        const includeRewriterIds = data?.includeRewriterIds ?? false

        // api call
        const res = await whatsAppService.getWhatsAppTemplatesForLocationId(locationId, templateId, includeRewriterIds)

        // data manipulation
        if (templateId) {
          return Promise.resolve(res)
        } else {
          commit('setTemplates', res)
          commit('setLocationId', locationId)
          return Promise.resolve(res)
        }
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getTemplateFolders({ commit, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const folderId = (data?.folderId as string) || null
        const res = await whatsAppService.getTemplateFolders(locationId)
        commit('setTemplateFolders', res)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createTemplateFolder({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.createTemplateFolder(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateTemplateFolder({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.updateTemplateFolder(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deleteTemplateFolder({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.deleteTemplateFolder(locationId, data.folderId, data?.parentFolderId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async addTemplateToFolder({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.addTemplateToFolder(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createWhatsAppTemplate({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.createWhatsAppTemplateForLocationId(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateWhatsAppTemplate({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.updateWhatsAppTemplateForLocationId(locationId, data.templateId, data.data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deleteWhatsAppTemplate({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.deleteWhatsAppTemplateForLocationId(
          locationId,
          data.templateId,
          data.templateName
        )
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getProduct(_, data) {
      try {
        const res = await whatsAppService.getProduct(data?.locationId ?? '')
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getCustomValueTags() {
      try {
        const res = await getTagOptions()
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getLocationPricing({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getLocationResellingConfiguration(locationId)
        commit('setOfferingEnabled', res.offeringEnabled)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async locationSub({ dispatch, commit, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.locationSub(locationId, data)
        if (res.paymentStatus === 'COMPLETE' || res.paymentStatus === 'COMPLETE_PAST_DUE') {
          commit('setIsSubscribed', true)
        }
        if (res.paymentStatus === 'PENDING') {
          commit('setIsPaymentPending', true)
        }
        await dispatch('isConfigured')
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getCompanySubscriptions({ rootState }) {
      try {
        const companyId = rootState?.company.company.id as string
        const res = await whatsAppService.getCompanySubscriptions(companyId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getLocationSubscriptions({ commit, dispatch, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getLocationSubscriptions(locationId)
        const isSubscribed = res[0]?.paymentStatus === 'COMPLETE' || res[0]?.paymentStatus === 'COMPLETE_PAST_DUE'
        const isPaymentPending = res[0]?.paymentStatus === 'PENDING'
        commit('setIsSubscribed', isSubscribed)
        commit('setIsPaymentPending', isPaymentPending)
        if (isSubscribed) {
          await dispatch('isConfigured')
        }
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableWhatsAppConversationRebilling({ rootState }) {
      try {
        const companyId = rootState?.company.company.id as string
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.enableWhatsAppConversationRebilling(companyId, locationId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async upload({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.upload(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getSignedUrl({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getSignedUrl(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async uploadToBucket(_, data) {
      try {
        // const locationId = rootState?.locations.currentLocation.id as string
        const res = await mediaService.uploadToBucket(data.file, data.signedUrl)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async verifyBucketUpload({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await mediaService.verify(data.aknId, locationId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async postAttachment(_, data) {
      try {
        const res = await whatsAppService.postAttachement(data.locationId, data.conversationId, data.formData)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async voiceNote({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.voiceNote(
          {
            locationId,
            userId: data.userId,
            contactId: data.contactId,
            conversationId: data.conversationId,
          },
          data.formData
        )
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getWhatsappBusinessProfileData({ commit, rootState }, data) {
      try {
        const res = await whatsAppService.getWhatsappBusinessProfileData(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async postWhatsappBusinessProfileData(_, data) {
      try {
        const res = await whatsAppService.postWhatsappBusinessProfileData(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async addPhoneNumber({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.addPhoneNumber(locationId, data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async sendOtp(_, data) {
      try {
        const res = await whatsAppService.sendOtp(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async verifyOtp(_, data) {
      try {
        const res = await whatsAppService.verifyOtp(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getAnalytics(_, data) {
      try {
        const res = await whatsAppService.getAnalytics(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async register({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.register(data, locationId)

        // we dont need to wait for this to complete, it will be done in the background
        whatsAppService.setupFlows(locationId)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getCalendarsData({ rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await CalendarService.getCalendars({
          locationId,
        })
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createFlowToken({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        data.locationId = locationId
        const res = await whatsAppService.createFlowToken(data)
        return res
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getFlows({ rootState, commit }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string

        // if fetchFromStore is true and locationId is the same as the one in the store, return the templates from the store
        if (data?.fetchFromStore === true && rootState?.whatsAppSettings.locationId === locationId) {
          const flows = rootState?.whatsAppSettings.flows
          if (flows.length > 0) {
            return Promise.resolve(flows)
          }
        }

        // api call
        const res = await whatsAppService.getFlows(locationId)

        // data manipulation
        commit('setFlows', res)
        commit('setLocationId', locationId)
        return res
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createFlow({}, data) {
      try {
        const res = await whatsAppService.createFlow(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateFlow({ rootState }, data) {
      try {
        const locationId = rootState?.locations?.currentLocation.id as string
        data.locationId = locationId
        const res = await whatsAppService.updateFlow(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateFlowAssets({ rootState }, data) {
      try {
        const locationId = rootState.locations?.currentLocation.id as string
        data.locationId = locationId
        const res = await whatsAppService.updateFlowAssets(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async flowPreview({ rootState }, data) {
      try {
        const locationId = rootState.locations.currentLocation.id as string
        data.locationId = locationId
        const res = await whatsAppService.flowPreview(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async flowPublish(_, data) {
      try {
        const res = await whatsAppService.flowPublish(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deprecateFlow(_, data) {
      try {
        const res = await whatsAppService.deprecateFlow(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deleteFlow({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        data.locationId = locationId
        const res = await whatsAppService.deleteFlow(data)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async cancelSubcription({ commit, dispatch, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.cancelSubscription(locationId, data.reason, data.disconnectWaba)
        commit('setIsSubscribed', false)
        await dispatch('isConfigured')
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async getOnboardingChecklist({ commit, rootState }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getOnboardingChecklist(locationId)
        commit('setOnboardingChecklist', res)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async templateRewriter({ commit, dispatch, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.templateRewriter({
          locationId,
          templateIds: data.templateIds,
        })
        await dispatch('getWhatsAppTemplates', {
          includeRewriterIds: true,
        })
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getCountdownLinks({ commit, rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const { linkIds = [], ...rest } = data ?? {}

        const response = await whatsAppService.getCountdownLinks({
          locationId,
          ...rest,
        })

        const timersRaw: CountdownTimerApiResponse[] = response as CountdownTimerApiResponse[]

        const timersMap = new Map<string, CountdownTimerLink>()
        timersRaw.forEach(timer => {
          const normalized = normalizeCountdownTimer(timer)
          if (normalized) {
            timersMap.set(normalized.linkId, normalized)
          }
        })

        const normalizedRequestedIds = Array.isArray(linkIds)
          ? toNormalizedIdSet(linkIds as Array<string | number>)
          : []

        const missingIds = normalizedRequestedIds.filter(id => !timersMap.has(id))

        if (missingIds.length) {
          const results = await Promise.allSettled(
            missingIds.map(id =>
              whatsAppService.getCountdownLink({
                locationId,
                linkId: id,
              })
            )
          )

          results.forEach((result, index) => {
            const requestedId = missingIds[index]

            if (result.status === 'fulfilled' && result.value) {
              const normalized = normalizeCountdownTimer(result.value as CountdownTimerApiResponse)
              if (normalized) {
                timersMap.set(normalized.linkId, normalized)
              }
            } else if (result.status === 'rejected') {
              console.error('[whatsAppSettings] Failed to fetch countdown timer by id', {
                linkId: requestedId,
                error: result.reason,
              })
            }
          })
        }

        const countdownLinks = Array.from(timersMap.values())
        commit('setCountdownLinks', countdownLinks)
        return Promise.resolve(countdownLinks)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getCountdownLink({ rootState }, data) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getCountdownLink({
          locationId,
          ...data,
        })
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async getMessagingAnalytics({ rootState }, data: { startDate: string; endDate: string }) {
      try {
        const locationId = rootState?.locations.currentLocation.id as string
        const res = await whatsAppService.getMessagingAnalytics({
          locationId,
          startDate: data.startDate,
          endDate: data.endDate,
        })
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
  },
}
